import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Stack,
  Chip,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  Alert,
} from '@mui/material';
import { collection, getDocs, getFirestore, doc, getDoc, updateDoc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { TableSkeleton } from '../components/loading';
import firebaseApp from '../utils/firebaseConfig';
import { formatPhoneDisplay } from '../utils/phone';

const db = getFirestore(firebaseApp);
const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';
const STRIPE_PK = 'pk_live_51Q5pO5RqULsyaFnGO4c7JjHvqYv9Eu4FovYOlgCfuWZyrpYgn33p1cZavi28z41Nxpo3PiKLbscqL7c2ezQUFux400HaYeh7o6';

const STATUS_META = {
  pending: { label: 'Pendiente', color: 'warning' },
  approved: { label: 'Aprobada', color: 'success' },
  rejected: { label: 'Rechazada', color: 'error' },
  payment_failed: { label: 'Pago falló', color: 'error' },
};

// Tipo de solicitud: compra normal (default), compra directa ya pagada en la
// tienda (autoApproved) o solicitud de cancelación enviada desde la app.
const requestKind = (req) => {
  if (req.requestType === 'cancel') return 'cancel';
  if (req.autoApproved) return 'purchase';
  return 'request';
};

const formatPrice = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = typeof ts.seconds === 'number' ? new Date(ts.seconds * 1000) : new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-PR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: 'numeric', minute: '2-digit' });
};

export default function MembershipRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Diálogo de evidencia
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [evidenceImages, setEvidenceImages] = useState([]);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceRequest, setEvidenceRequest] = useState(null);

  // Aprobación en curso (id de la solicitud que se está procesando)
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'membership_requests'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setRequests(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    } catch (error) {
      console.error('Error fetching membership requests:', error);
    }
    setLoading(false);
  };

  const counts = requests.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  const filtered = statusFilter === 'all'
    ? requests
    : statusFilter === 'purchases'
      ? requests.filter((r) => requestKind(r) === 'purchase')
      : requests.filter((r) => r.status === statusFilter);

  const openEvidence = async (req) => {
    setEvidenceRequest(req);
    setEvidenceOpen(true);
    setEvidenceLoading(true);
    setEvidenceImages([]);
    try {
      const ids = req.evidenceIds || [];
      const imgs = await Promise.all(ids.map(async (id) => {
        const snap = await getDoc(doc(db, 'membership_request_evidence', id));
        return snap.exists() ? { id, ...snap.data() } : null;
      }));
      setEvidenceImages(imgs.filter(Boolean));
    } catch (error) {
      console.error('Error loading evidence:', error);
    }
    setEvidenceLoading(false);
  };

  // Aprobar = AQUÍ se hace el cobro real a la tarjeta guardada del cliente y
  // se activa la membresía. Usa las mismas functions que la compra normal.
  // Total a cobrar = precio + IVU 11.5% (solicitudes viejas no traen totalCents)
  const requestTotalCents = (req) => (Number(req.totalCents) > 0
    ? Number(req.totalCents)
    : Math.round((req.priceCents || 0) * 1.115));

  // Cancelación pedida desde la app: NO se cobra nada; al procesarla se
  // desactiva la membresía del cliente.
  const handleProcessCancel = async (req) => {
    if (!window.confirm(`¿Procesar la cancelación de la membresía de "${req.memberName}"?\n\nSe marcará la membresía como INACTIVA. No se hace ningún cobro ni reembolso automático.`)) return;
    setProcessingId(req.id);
    try {
      if (req.memberId) {
        await updateDoc(doc(db, 'members', req.memberId), { active: false, autoRenew: false, autoRenewDisabledReason: 'cancelled_by_customer', updatedAt: serverTimestamp() });
      }
      await updateDoc(doc(db, 'membership_requests', req.id), {
        status: 'approved',
        decidedAt: serverTimestamp(),
      });
      await fetchRequests();
    } catch (error) {
      console.error('Error procesando cancelación:', error);
      alert(`Error procesando la cancelación: ${error.message}`);
    }
    setProcessingId(null);
  };

  const handleApprove = async (req) => {
    if (requestKind(req) === 'cancel') { handleProcessCancel(req); return; }
    if (!window.confirm(`¿Aprobar la solicitud de "${req.memberName}" para "${req.planName}"?\n\nSe cobrará ${formatPrice(requestTotalCents(req))} (incluye IVU 11.5%) a la tarjeta ${req.paymentMethodLabel || 'guardada'} y se activará la membresía.`)) return;
    setProcessingId(req.id);
    try {
      if (!req.stripeCustomerId || !req.paymentMethodId || !req.planId) {
        alert('A esta solicitud le falta información de pago. Pide al cliente que la vuelva a enviar.');
        setProcessingId(null);
        return;
      }

      // Pago único (sin Stripe Subscriptions). forceRenewal respeta el precio
      // pactado cuando el cliente envió la solicitud (regular o renovación).
      const payRes = await axios.post(`${FUNCTIONS_BASE}/createMembershipPayment`, {
        stripeCustomerId: req.stripeCustomerId,
        planId: req.planId,
        memberId: req.memberId,
        memberEmail: req.memberEmail || '',
        forceRenewal: !!req.isRenewal,
      });
      const { clientSecret, paymentIntentId } = payRes.data;

      if (clientSecret) {
        const stripeObj = await loadStripe(STRIPE_PK);
        const { error: confirmError, paymentIntent } = await stripeObj.confirmCardPayment(clientSecret, {
          payment_method: req.paymentMethodId,
        });

        if (confirmError) {
          await updateDoc(doc(db, 'membership_requests', req.id), {
            status: 'payment_failed',
            paymentError: confirmError.message || 'Error desconocido',
            decidedAt: serverTimestamp(),
          });
          alert(`El cobro a la tarjeta del cliente falló: ${confirmError.message}\n\nLa solicitud quedó marcada como "Pago falló". Contacta al cliente para que actualice su tarjeta.`);
          await fetchRequests();
          setProcessingId(null);
          return;
        }

        if (paymentIntent.status === 'succeeded') {
          await axios.post(`${FUNCTIONS_BASE}/activateMemberAfterPayment`, {
            memberId: req.memberId,
            membershipPlanId: req.planId,
            paymentIntentId,
          });
          await updateDoc(doc(db, 'membership_requests', req.id), {
            status: 'approved',
            paymentIntentId: paymentIntentId || '',
            decidedAt: serverTimestamp(),
          });
          alert(`✅ Solicitud aprobada. Se cobró ${formatPrice(requestTotalCents(req))} y la membresía de ${req.memberName} quedó activa.`);
          await fetchRequests();
        }
      }
    } catch (error) {
      console.error('Error approving request:', error);
      alert(`Error aprobando la solicitud: ${error.response?.data?.error || error.message}`);
    }
    setProcessingId(null);
  };

  const handleReject = async (req) => {
    const reason = window.prompt(`¿Por qué se rechaza la solicitud de "${req.memberName}"?\n(La razón queda guardada; al cliente NO se le cobra nada)`);
    if (reason === null) return; // canceló
    setProcessingId(req.id);
    try {
      await updateDoc(doc(db, 'membership_requests', req.id), {
        status: 'rejected',
        rejectReason: reason.trim(),
        decidedAt: serverTimestamp(),
      });
      await fetchRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Error rechazando la solicitud');
    }
    setProcessingId(null);
  };

  const purchasesCount = requests.filter((r) => requestKind(r) === 'purchase').length;
  const FILTERS = [
    { key: 'pending', label: `Pendientes (${counts.pending || 0})` },
    { key: 'approved', label: `Aprobadas (${counts.approved || 0})` },
    { key: 'purchases', label: `Compras directas (${purchasesCount})` },
    { key: 'rejected', label: `Rechazadas (${counts.rejected || 0})` },
    { key: 'payment_failed', label: `Pago falló (${counts.payment_failed || 0})` },
    { key: 'all', label: `Todas (${requests.length})` },
  ];

  return (
    <>
      <Helmet>
        <title>Solicitudes de Membresía | BBTC Dashboard</title>
      </Helmet>

      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4">Solicitudes de Membresía</Typography>
          <Button variant="outlined" startIcon={<Iconify icon="eva:refresh-fill" />} onClick={fetchRequests}>
            Refrescar
          </Button>
        </Stack>

        <Alert severity="info" sx={{ mb: 3 }}>
          Solicitudes de membresías con tarifa especial (menores, policías/agentes) hechas desde la tienda online.
          Al <strong>aprobar</strong> se cobra la tarjeta guardada del cliente y se activa la membresía.
          Al <strong>rechazar</strong> no se cobra nada. Las <strong>compras directas</strong> (pagadas al momento en la
          tienda) también salen aquí, ya aprobadas, para saber quién compró. Las <strong>cancelaciones</strong> pedidas
          desde la app se procesan aquí (desactiva la membresía; sin cobro).
        </Alert>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {FILTERS.map((f) => (
            <Chip
              key={f.key}
              label={f.label}
              color={statusFilter === f.key ? 'primary' : 'default'}
              variant={statusFilter === f.key ? 'filled' : 'outlined'}
              onClick={() => { setStatusFilter(f.key); setPage(0); }}
            />
          ))}
        </Stack>

        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 900 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Plan</TableCell>
                    <TableCell>Precio</TableCell>
                    <TableCell>Tarjeta</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && <TableSkeleton rows={5} columns={7} />}
                  {!loading && filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((req) => {
                    const meta = STATUS_META[req.status] || { label: req.status, color: 'default' };
                    const busy = processingId === req.id;
                    return (
                      <TableRow hover key={req.id}>
                        <TableCell>{formatDate(req.createdAt)}</TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">{req.memberName || '—'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatPhoneDisplay(req.memberPhone)} {req.memberEmail ? `· ${req.memberEmail}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {req.planName}
                          {requestKind(req) === 'purchase' && !req.autoRenewal && (
                            <Chip label="Compra directa (pagada)" size="small" color="info" variant="outlined" sx={{ display: 'flex', width: 'fit-content', mt: 0.5 }} />
                          )}
                          {req.autoRenewal && (
                            <Chip label="Renovación automática" size="small" color="secondary" variant="outlined" sx={{ display: 'flex', width: 'fit-content', mt: 0.5 }} />
                          )}
                          {requestKind(req) === 'cancel' && (
                            <>
                              <Chip label="Cancelación (app)" size="small" color="error" variant="outlined" sx={{ display: 'flex', width: 'fit-content', mt: 0.5 }} />
                              {req.reason && <Typography variant="caption" color="text.secondary" display="block">Motivo: {req.reason}</Typography>}
                            </>
                          )}
                        </TableCell>
                        <TableCell>
                          {requestKind(req) === 'cancel' ? '—' : formatPrice(requestTotalCents(req))}
                          {requestKind(req) !== 'cancel' && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {formatPrice(req.priceCents)} + IVU
                            </Typography>
                          )}
                          {req.isRenewal && (
                            <Chip label="Renovación" size="small" color="success" variant="outlined" sx={{ ml: 0.5 }} />
                          )}
                        </TableCell>
                        <TableCell>{req.paymentMethodLabel || '—'}</TableCell>
                        <TableCell>
                          <Chip label={meta.label} color={meta.color} size="small" />
                          {req.status === 'rejected' && req.rejectReason && (
                            <Typography variant="caption" display="block" color="text.secondary">{req.rejectReason}</Typography>
                          )}
                          {req.status === 'payment_failed' && req.paymentError && (
                            <Typography variant="caption" display="block" color="error">{req.paymentError}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button size="small" variant="outlined" onClick={() => openEvidence(req)} disabled={!(req.evidenceIds || []).length}>
                              Ver evidencia
                            </Button>
                            {(req.status === 'pending' || req.status === 'payment_failed') && (
                              <>
                                <Button size="small" variant="contained" color={requestKind(req) === 'cancel' ? 'error' : 'success'} onClick={() => handleApprove(req)} disabled={busy}>
                                  {busy ? <CircularProgress size={18} color="inherit" /> : (requestKind(req) === 'cancel' ? 'Procesar cancelación' : 'Aprobar y cobrar')}
                                </Button>
                                {req.status === 'pending' && (
                                  <Button size="small" variant="outlined" color="error" onClick={() => handleReject(req)} disabled={busy}>
                                    Rechazar
                                  </Button>
                                )}
                              </>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!loading && filtered.length === 0 && (
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No hay solicitudes {statusFilter !== 'all' ? `con status "${STATUS_META[statusFilter]?.label || statusFilter}"` : ''}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setPage(0); setRowsPerPage(parseInt(e.target.value, 10)); }}
          />
        </Card>
      </Container>

      <Dialog open={evidenceOpen} onClose={() => setEvidenceOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Evidencia — {evidenceRequest?.memberName} ({evidenceRequest?.planName})
        </DialogTitle>
        <DialogContent dividers>
          {evidenceLoading && (
            <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
          )}
          {!evidenceLoading && evidenceImages.length === 0 && (
            <Typography variant="body2" color="text.secondary">No se encontró la evidencia.</Typography>
          )}
          {!evidenceLoading && evidenceImages.map((img) => (
            <Box key={img.id} sx={{ mb: 2, textAlign: 'center' }}>
              <Box component="img" src={img.dataUrl} alt={img.name || 'evidencia'} sx={{ maxWidth: '100%', borderRadius: 1 }} />
              {img.name && <Typography variant="caption" display="block" color="text.secondary">{img.name}</Typography>}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvidenceOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
